import React from 'react';
import { FaGithub, FaLinkedin, FaDownload } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import headshot from '../../resources/intro/headshot.jpg';
import resumePdf from '../../resources/footer/resume.pdf';
import gwLogo from '../../resources/projects/gw-logo.png';
import metaLogo from '../../resources/projects/meta.png';
import pennLogo from '../../resources/projects/penn.png';
import salesforceLogo from '../../resources/projects/salesforce.webp';
import itbaLogo from '../../resources/projects/itba.png';
import './About.css';

const experience = [
  {
    company: 'Gravity Well',
    logo: gwLogo,
    role: 'Junior Engineer',
    roleNote: 'Promoted from internship',
    period: 'May 2025 – Present',
    location: 'Los Angeles, CA',
    bullets: [
      'Co-led a strike team to add split screen for a AAA multiplayer title, resolving authentication issues across player sessions, fixing controller input registration per player, and isolating post-processing effects to specific viewports.',
      'Diagnosed GPU-bound performance issues on Steam Deck (RDNA2) analyzing memory dumps and shader performance, auditing instancing usage, and tuning engine/rendering configuration to go from 15fps to a stable 40fps.',
      'Collaborated with tech art to build an interactive foliage system using Niagara and a ping-pong render target approach, enabling real-time foliage response to player interaction.',
      'Contributed to gameplay and engine systems during an initial internship, working alongside engineers, designers, and artists on a AAA new IP multiplayer title in Unreal Engine (C++).'
    ]
  },
  {
    company: 'Meta',
    logo: metaLogo,
    role: 'Technical Artist Intern',
    period: 'Aug – Oct 2025',
    location: 'Bay Area, CA',
    bullets: [
      'Engineered a production-ready real-time VFX tool from a proof-of-concept shader, writing GLSL-based billboarding and control parameter systems to enable rapid creation of 10+ new Avatar Editor effects.'
    ]
  },
  {
    company: 'University of Pennsylvania',
    logo: pennLogo,
    role: 'Teaching Assistant',
    period: 'Jan 2025 – May 2026',
    location: 'Philadelphia, PA',
    bullets: [
      'Advanced Computer Rendering and Interactive Computer Graphics: mentored students through C++/GLSL implementations of rendering pipelines, path tracers, and PBR/IBL shaders; reviewed code for mathematical correctness and quality.'
    ]
  },
  {
    company: 'Salesforce',
    logo: salesforceLogo,
    role: 'Mid-Level Senior Software Engineer',
    roleNote: 'Progressed from intern to mid-level over 4 years',
    period: 'Mar 2020 – Jul 2024',
    location: 'Buenos Aires, Argentina',
    bullets: [
      "Led the redesign of Mulesoft Anypoint Exchange's asset detail page with full ownership from UX collaboration through React/TypeScript implementation, driving a significant increase in user engagement.",
      'Developed and maintained large-scale distributed services using agile methodologies and modern cloud tooling (Kubernetes), ensuring stability and scalability for production systems.',
      'Led search and discovery features end-to-end: ElasticSearch-backed filters in React/TypeScript and Node.js, with A/B testing improving average click rank by 20% and reducing zero-result searches by 5%.'
    ]
  }
];

const education = [
  {
    school: 'University of Pennsylvania',
    logo: pennLogo,
    degree: 'M.S.E. Computer Graphics & Game Technology',
    period: 'Aug 2024 – May 2026',
    location: 'Philadelphia, PA',
    note: '4.0 GPA · Jorge and Maria Rosas Fellow'
  },
  {
    school: 'Instituto Tecnológico de Buenos Aires (ITBA)',
    logo: itbaLogo,
    degree: 'Software Engineer — B.S. + M.S. equivalent',
    period: 'Mar 2015 – Dec 2020',
    location: 'Buenos Aires, Argentina',
    note: ''
  }
];

const skills: Record<string, string[]> = {
  Programming: ['C', 'C++', 'C#', 'GLSL', 'JavaScript/TypeScript', 'Python'],
  Graphics: [
    'OpenGL',
    'Real-time & offline rendering',
    'PBR/IBL',
    'Deferred rendering',
    'Shader pipelines',
    'CPU/GPU profiling'
  ],
  'Engines & Tools': ['Unreal Engine (C++)', 'Unity (C#)', 'Qt', 'Git', 'Perforce', 'AWS', 'Agile']
};

const About: React.FC = () => {
  return (
    <div className="about-container">
      {/* Profile header: avatar + name + links */}
      <section className="about-header">
        <img src={headshot} alt="Constanza De Rienzo" className="about-avatar" />
        <div className="about-header-info">
          <h1 className="about-name">Constanza De Rienzo</h1>
          <p className="about-meta">
            M.S.E. Computer Graphics & Game Technology · University of Pennsylvania
          </p>
          <div className="about-links">
            <a
              href="https://github.com/constanzaderienzo"
              target="_blank"
              rel="noopener noreferrer"
              className="about-link">
              <FaGithub size={14} /> GitHub
            </a>
            <a
              href="https://www.linkedin.com/in/constanza-de-rienzo/"
              target="_blank"
              rel="noopener noreferrer"
              className="about-link">
              <FaLinkedin size={14} /> LinkedIn
            </a>
            <a href="mailto:derienzoconstanza@gmail.com" className="about-link">
              <MdEmail size={14} /> derienzoconstanza@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* Bio */}
      <p className="about-bio">
        I'm a software engineer specializing in computer graphics and game technology, recently
        graduated from the University of Pennsylvania's M.S.E. program (4.0 GPA, Jorge and Maria
        Rosas Fellow). My work spans real-time rendering, game engine systems, and multiplayer
        networking — from a Monte Carlo path tracer and PBR/IBL pipeline built from scratch, to a
        persistent snow-deformation system in Unreal Engine, to a networked multiplayer game where I
        owned the server-authoritative gameplay and inventory architecture. I'm based in Los Angeles
        and looking for roles at the intersection of graphics engineering, game engine development,
        and interactive technology.
      </p>

      {/* Experience */}
      <section className="about-section">
        <h2 className="about-section-label">Experience</h2>
        <div className="about-timeline">
          {experience.map((item) => (
            <div key={item.company + item.period} className="about-timeline-item">
              {item.logo ? (
                <img src={item.logo} alt={item.company} className="about-timeline-logo" />
              ) : (
                <div className="about-timeline-dot" />
              )}
              <div className="about-timeline-body">
                <div className="about-timeline-header">
                  <span className="about-timeline-title">{item.company}</span>
                  <span className="about-timeline-period">{item.period}</span>
                </div>
                <p className="about-timeline-sub">{item.role}</p>
                {'roleNote' in item && item.roleNote && (
                  <p className="about-timeline-role-note">{item.roleNote}</p>
                )}
                {item.location && <p className="about-timeline-location">{item.location}</p>}
                {item.bullets.length > 0 && (
                  <ul className="about-timeline-bullets">
                    {item.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Education */}
      <section className="about-section">
        <h2 className="about-section-label">Education</h2>
        <div className="about-timeline">
          {education.map((item) => (
            <div key={item.school} className="about-timeline-item">
              {'logo' in item && item.logo ? (
                <img src={item.logo} alt={item.school} className="about-timeline-logo" />
              ) : (
                <div className="about-timeline-dot" />
              )}
              <div className="about-timeline-body">
                <div className="about-timeline-header">
                  <span className="about-timeline-title">{item.school}</span>
                  <span className="about-timeline-period">{item.period}</span>
                </div>
                <p className="about-timeline-sub">{item.degree}</p>
                {item.location && <p className="about-timeline-location">{item.location}</p>}
                {item.note && <p className="about-timeline-note">{item.note}</p>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Skills */}
      <section className="about-section">
        <h2 className="about-section-label">Technical Skills</h2>
        {Object.entries(skills).map(([group, items]) => (
          <div key={group} className="about-skills-group">
            <p className="about-skills-group-label">{group}</p>
            <div className="about-skills-pills">
              {items.map((skill) => (
                <span key={skill} className="about-skill-pill">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Resume */}
      <a
        href={resumePdf}
        download="Constanza_De_Rienzo_Resume.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="about-resume-btn">
        <FaDownload size={12} /> Download Resume
      </a>
    </div>
  );
};

export default About;
